import { useState } from "react";
import { createPortal } from "react-dom";
import { CreateMilestone } from "./CreateMilestone";
import { GeneralModal } from "../GeneralModal/GeneralModal";
import { useSelector } from "react-redux";
import {
  useDeleteMilestoneMutation,
  useGetMilestonesQuery,
} from "../../../../store/apiSlice/newInterfaceApiSlice";
import styled from "styled-components";
import { DeleteModal } from "../DeleteModal/DeleteModal";
import { orderTypeForUrl, selectState } from "../../page/utils/tableDataElements";
import { CrudCategory } from "../../page/components/CrudCategory/CrudCategory";
import { colorcomb } from "./constant";

/**
 * Milestone Component
 *
 * Manages creation, editing, and deletion of milestones in modules like shipment, purchase, or transfer.
 * Handles multiple modal states: a list of milestones, a create/edit milestone form, and a confirmation
 * modal for deletions. Integrates with Redux to fetch and update milestone data.
 *
 * @component
 *
 * @param {Function} closeMilestoneModal - Closes the milestones list modal.
 * @param {boolean} isOpenMilestone - Controls visibility of the milestones list modal.
 * @param {string} orderType - Type of order context, e.g. "shipment", "purchase", determines state and API.
 *
 *
 */

const emptyMilestone = { name: "", color: "processing" };

export const Milestone = ({ closeMilestoneModal, isOpenMilestone, orderType }) => {
  const [milestoneName, setMilestoneName] = useState(emptyMilestone);
  const [isOpenCreateMilestone, setIsOpenCreateMilestone] = useState(false);
  const [deleteMilestone, { isLoading: deleting }] = useDeleteMilestoneMutation();
  useGetMilestonesQuery(new URLSearchParams(orderTypeForUrl[orderType]).toString());
  const { milestoneList } = useSelector(state => {
    if (state[selectState[orderType]]) return state[selectState[orderType]];
    return { milestoneList: [] };
  });

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const closeCreateMilestone = () => {
    setIsOpenCreateMilestone(false);
  };

  const handleMilestoneModal = () => {
    setIsOpenCreateMilestone(true);
    setMilestoneName(emptyMilestone);
  };

  const handleEdit = (val, ind) => {
    setMilestoneName({ name: val.name, color: val.meta?.color || "processing", id: ind });
    setIsOpenCreateMilestone(true);
  };

  const closeDeleteModal = () => setIsOpenDeleteModal(false);
  const openDeleteModal = (id, name) => {
    setIsOpenDeleteModal(true);
    setDeleteData({ id, name });
  };

  const closeModal = () => {
    setIsOpenCreateMilestone(false);
    setMilestoneName(emptyMilestone);
    closeMilestoneModal();
  };

  /**
   * Closes confirmation only after the milestone has been deleted.
   * @return {Promise<void>} Completion of the deletion attempt.
   */
  const handleDeleteModal = async () => {
    if (deleting) return;
    try {
      await deleteMilestone(deleteData.id).unwrap();
      closeDeleteModal();
    } catch (_) {
      // The mutation displays the server error and confirmation remains open.
    }
  };

  if (!isOpenMilestone) return null;

  const modalRoot = document.getElementById("root-modals") || document.body;

  return createPortal(
    <MilestoneOverlay
      onMouseDown={event => {
        if (event.target === event.currentTarget) closeModal();
      }}
    >
      {isOpenDeleteModal && (
        <DeleteModal
          handleDeleteModal={handleDeleteModal}
          closeDeleteModal={closeDeleteModal}
          nameOfView={`Think carefully! Deleting this "${deleteData.name}" milestone is permanent, and the data will be lost forever.`}
          labelForConfirmCheck={`I understand that the data cannot be retrieved after deleting.`}
        />
      )}
      {isOpenCreateMilestone ? (
        <CreateMilestone
          closeCreateMilestone={closeCreateMilestone}
          milestoneName={{ ...milestoneName }}
          orderType={orderType}
        />
      ) : (
        <GeneralModal
          titleOfModal="Milestones"
          closeModal={closeModal}
          widthOfGeneralModal="420px"
          classNameOfModal="milestoneModal"
        >
          <CrudCategory
            variant="panel"
            handleFileCategoryModal={handleMilestoneModal}
            btnNameForAddCat="Add milestone"
            handleEdit={handleEdit}
            openDeleteModal={openDeleteModal}
            listOfCat={milestoneList || []}
            isShowDeleteEditBtn={true}
            emptyLabel="No milestones yet."
            renderLeading={item => {
              const color = colorcomb[item?.meta?.color]?.color;
              if (!color) return null;
              return <span className="itemLeading" style={{ backgroundColor: color }} />;
            }}
          />
        </GeneralModal>
      )}
    </MilestoneOverlay>,
    modalRoot,
  );
};

const MilestoneOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 36, 77, 0.28);

  .milestoneModal {
    border-radius: 8px;
    box-shadow: 0 18px 48px rgba(16, 36, 77, 0.18);
  }

  .milestoneModal > .generalContent {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .milestoneModal > h1 {
    border-radius: 8px 8px 0 0;
  }

  .milestoneModal > .createMilestone:not(:has(button)) {
    display: none;
  }

  .milestoneModal .labelForText {
    gap: 6px;
  }

  .milestoneModal .labelForText > span {
    color: #4a4a68;
    font-size: 12px;
    font-weight: 600;
  }

  .milestoneModal .labelForText .inputBox {
    height: 36px;
    padding: 8px 12px;
    border-radius: 6px;
    border-color: #d5deea;
    color: #10244d;
  }

  .milestoneModal .createMilestone {
    padding: 4px 16px 16px;
  }

  .milestoneModal .createMilestone button {
    height: 32px;
    border-radius: 6px;
    font-weight: 600;
  }

  .formError {
    margin: 0;
    color: #cf324a;
    font-size: 13px;
    line-height: 20px;
  }

  .colorPicker {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .colorLabel {
    color: #4a4a68;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
  }

  .colorName {
    margin: 0;
    color: #676879;
    font-size: 12px;
    line-height: 18px;
    text-transform: capitalize;
  }

  .allColor {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .colorBox {
    box-sizing: border-box;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    appearance: none;
    box-shadow: inset 0 0 0 1px rgba(16, 36, 77, 0.08);

    &.selected {
      border-color: #10244d;
      box-shadow: 0 0 0 2px #fff inset;
    }
  }
`;
